import React, { useEffect, useState, useCallback } from "react";
import PrimaryButton from "../../shared/buttons/primaryButton";
import { useEmpoyees } from "../../../api/hooks";
import debounce from "lodash/debounce";

const AddEmployee = ({
  onChange,
  defaultSelectedEmployee,
  label = "Team",
  compact = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState(
    defaultSelectedEmployee || []
  );

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setDebouncedSearchTerm(value);
    }, 500),
    []
  );

  const { data: employeesData, isLoading } = useEmpoyees(null, null, debouncedSearchTerm, "select");
  const employeesList = employeesData?.employees || [];

  // Filter employees based on search term
  const filteredEmployees = employeesList.filter((employee) =>
    employee?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee?.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
    setShowDropdown(true);
  };

  // Handle employee selection
  const handleEmployeeSelect = (employee) => {
    if (!selectedEmployees.some((emp) => emp._id === employee._id)) {
      setSelectedEmployees([...selectedEmployees, employee]);
    } else {
      setSelectedEmployees(
        selectedEmployees.filter((emp) => emp._id !== employee._id)
      );
    }
  };

  // Handle add button click
  const handleAddClick = () => {
    setShowDropdown(false);
    setSearchTerm("");
    setDebouncedSearchTerm("");
  };

  useEffect(() => {
    onChange(selectedEmployees);
  }, [selectedEmployees]);

  return (
    <div className={`flex relative flex-col gap-y-2 ${compact ? "" : "min-h-60"}`}>
      <label className="text-sm pl-2 font-bold text-gray-500">{label}</label>
      <div className="w-full gap-x-2 relative flex items-start">
        <div className="w-full">
          <input
            type="text"
            className="rounded-lg text-sm border-2 text-gray-500 border-gray-200/80 py-2 px-4 outline-none focus:outline-none w-full"
            placeholder="Search by name, position or email"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => setShowDropdown(true)}
          />

          {/* Dropdown Results */}
          {showDropdown && searchTerm && (
            <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {isLoading ? (
                <div className="p-3 text-gray-500">Loading...</div>
              ) : filteredEmployees?.length > 0 ? (
                filteredEmployees.map((employee) => (
                  <div
                    key={employee._id}
                    className="flex items-center p-3 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleEmployeeSelect(employee)}
                  >
                    <input
                      type="checkbox"
                      className="mr-3"
                      checked={selectedEmployees.some(
                        (emp) => emp._id === employee._id
                      )}
                      onChange={() => { }}
                    />
                    <div className="flex flex-col">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-800">
                          {employee?.name}
                        </span>
                        <span className="text-xs rounded-lg ml-3 bg-gray-100 px-2">
                          {employee?.position}
                        </span>
                      </div>
                      {(employee?.email || employee?.level) && (
                        <div className="flex items-center mt-1">
                          <span className="text-xs text-gray-500">
                            {employee?.email}
                          </span>
                          <span className="text-xs rounded-lg ml-3 bg-blue-100 text-blue-800 px-2">
                            {employee?.level || 'No Level'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-3 text-gray-500">No employees found</div>
              )}
            </div>
          )}

          {/* Selected Employees */}
          {selectedEmployees.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedEmployees.map((employee) => (
                <div
                  key={employee?._id}
                  className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center"
                >
                  <span className="text-sm">{employee?.name}</span>
                  <button
                    className="ml-2 text-blue-600 hover:text-blue-800"
                    onClick={() => handleEmployeeSelect(employee)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <PrimaryButton
          title={"Add"}
          className="px-7 text-white"
          onclick={handleAddClick}
        />
      </div>
    </div>
  );
};

export default AddEmployee;
